import {
	Gauge,
	Pencil,
	Plus,
	Power,
	PowerOff,
	Search,
	Trash2,
	X,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import {
	useCreateCommitmentMatrix,
	useDeleteCommitmentMatrix,
	useUpdateCommitmentMatrix,
} from '@/api/queries/commitment-matrix-queries'
import {
	CommitmentMatrixFormContent,
	type CommitmentMatrixFormValues,
} from '@/components/administration/commitment-matrix/commitment-matrix-form'
import { ConfirmDialog, ResponsiveFormModal } from '@/components/commons'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { cn } from '@/lib/utils'
import type { CommitmentMatrix } from '@/types'

interface MatricesTabProps {
	matrices: CommitmentMatrix[]
	isLoading: boolean
}

type ScopeChip = 'personIds' | 'crIds' | 'jobGroupIds' | 'branchIds'

const SCOPE_CHIPS: { value: ScopeChip; label: string }[] = [
	{ value: 'personIds', label: 'Pessoas' },
	{ value: 'crIds', label: 'CRs' },
	{ value: 'jobGroupIds', label: 'Grupo' },
	{ value: 'branchIds', label: 'Filial' },
]

export function MatricesTab({ matrices, isLoading }: MatricesTabProps) {
	const createMatrixMutation = useCreateCommitmentMatrix()
	const updateMatrixMutation = useUpdateCommitmentMatrix()
	const deleteMatrixMutation = useDeleteCommitmentMatrix()

	const [matrixFormOpen, setMatrixFormOpen] = useState(false)
	const [editingMatrix, setEditingMatrix] = useState<
		CommitmentMatrix | undefined
	>(undefined)
	const [deletingMatrix, setDeletingMatrix] = useState<CommitmentMatrix | null>(
		null,
	)

	const [search, setSearch] = useState('')
	const [scopeFilters, setScopeFilters] = useState<ScopeChip[]>([])
	const [showInactive, setShowInactive] = useState(true)

	const filteredMatrices = useMemo(() => {
		const term = search.trim().toLowerCase()
		return matrices.filter((matrix) => {
			if (!showInactive && !matrix.active) return false
			if (term && !matrix.name.toLowerCase().includes(term)) return false
			if (
				scopeFilters.length > 0 &&
				!scopeFilters.every((chip) => matrix[chip].length > 0)
			)
				return false
			return true
		})
	}, [matrices, search, scopeFilters, showInactive])

	const isSavingMatrix =
		createMatrixMutation.isPending || updateMatrixMutation.isPending

	const handleOpenCreateMatrix = () => {
		setEditingMatrix(undefined)
		setMatrixFormOpen(true)
	}

	const handleOpenEditMatrix = (matrix: CommitmentMatrix) => {
		setEditingMatrix(matrix)
		setMatrixFormOpen(true)
	}

	const handleSaveMatrix = (values: CommitmentMatrixFormValues) => {
		if (editingMatrix) {
			updateMatrixMutation.mutate(
				{ id: editingMatrix.id, body: values },
				{
					onSuccess: () => {
						toast.success('Matriz atualizada.')
						setMatrixFormOpen(false)
						setEditingMatrix(undefined)
					},
					onError: () => toast.error('Não foi possível atualizar a matriz.'),
				},
			)
		} else {
			createMatrixMutation.mutate(values, {
				onSuccess: () => {
					toast.success('Matriz criada.')
					setMatrixFormOpen(false)
				},
				onError: () => toast.error('Não foi possível criar a matriz.'),
			})
		}
	}

	const handleToggleActive = (matrix: CommitmentMatrix) => {
		updateMatrixMutation.mutate(
			{ id: matrix.id, body: { active: !matrix.active } },
			{
				onError: () => toast.error('Não foi possível atualizar a matriz.'),
			},
		)
	}

	const handleConfirmDeleteMatrix = () => {
		if (!deletingMatrix) return

		deleteMatrixMutation.mutate(deletingMatrix.id, {
			onSuccess: () => {
				toast.success('Matriz excluída.')
				setDeletingMatrix(null)
			},
			onError: () => {
				toast.error('Não foi possível excluir a matriz.')
				setDeletingMatrix(null)
			},
		})
	}

	const hasActiveFilters =
		search.trim() !== '' || scopeFilters.length > 0 || !showInactive

	const handleClearFilters = () => {
		setSearch('')
		setScopeFilters([])
		setShowInactive(true)
	}

	return (
		<div className="flex flex-col gap-4">
			<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
				<div className="relative w-full sm:max-w-xs">
					<Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-kaefer-medium-grey" />
					<Input
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						placeholder="Buscar por nome..."
						className="pl-8"
					/>
				</div>
				<Button
					onClick={handleOpenCreateMatrix}
					className="gap-2 bg-kaefer-medium-grey text-white hover:bg-kaefer-medium-grey/90 w-full sm:w-auto"
				>
					<Plus className="size-4" />
					<span>Nova matriz</span>
				</Button>
			</div>

			<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
				<ToggleGroup
					type="multiple"
					variant="outline"
					size="sm"
					value={scopeFilters}
					onValueChange={(value) => setScopeFilters(value as ScopeChip[])}
					className="flex-wrap"
				>
					{SCOPE_CHIPS.map((chip) => (
						<ToggleGroupItem key={chip.value} value={chip.value}>
							{chip.label}
						</ToggleGroupItem>
					))}
				</ToggleGroup>

				<div className="flex items-center gap-4">
					<label className="flex items-center gap-2 text-sm text-kaefer-dark-grey">
						<Switch
							checked={showInactive}
							onCheckedChange={setShowInactive}
							size="sm"
						/>
						Mostrar inativas
					</label>
					{hasActiveFilters && (
						<Button
							type="button"
							variant="ghost"
							size="sm"
							onClick={handleClearFilters}
							className="gap-1 text-kaefer-medium-grey hover:bg-transparent hover:text-kaefer-medium-grey/70"
						>
							<X className="size-3.5" />
							Limpar filtros
						</Button>
					)}
				</div>
			</div>

			<div className="flex flex-col gap-3">
				{isLoading ? (
					Array.from({ length: 3 }).map((_, i) => (
						<Skeleton
							key={`matrix-skeleton-${i}`}
							className="h-28 w-full rounded-lg"
						/>
					))
				) : matrices.length === 0 ? (
					<p className="py-10 text-center text-sm text-muted-foreground">
						Nenhuma matriz cadastrada.
					</p>
				) : filteredMatrices.length === 0 ? (
					<p className="py-10 text-center text-sm text-muted-foreground">
						Nenhuma matriz encontrada para os filtros aplicados.
					</p>
				) : (
					filteredMatrices.map((matrix) => (
						<div
							key={matrix.id}
							className={cn(
								'rounded-lg border bg-white p-4',
								'border-kaefer-light-silver',
							)}
						>
							<div className="flex flex-wrap items-start justify-between gap-3">
								<div className="min-w-0">
									<div className="flex items-center gap-2">
										<span className="font-semibold text-kaefer-dark-grey">
											{matrix.name}
										</span>
										{!matrix.active && (
											<Badge className="bg-kaefer-light-silver text-muted-foreground">
												Inativa
											</Badge>
										)}
									</div>
									<div className="mt-1 flex items-center gap-1.5 text-sm text-kaefer-medium-grey">
										<Gauge className="size-3.5" />
										<span>{matrix.monthlyTarget} inspeções/mês</span>
									</div>
								</div>

								<div className="flex items-center gap-1">
									<Button
										type="button"
										variant="ghost"
										size="icon-circle"
										onClick={() => handleOpenEditMatrix(matrix)}
										className="text-kaefer-medium-grey hover:bg-transparent hover:text-kaefer-medium-grey/70"
										title="Editar matriz"
									>
										<Pencil className="size-4" />
									</Button>
									<Button
										type="button"
										variant="ghost"
										size="icon-circle"
										onClick={() => handleToggleActive(matrix)}
										disabled={updateMatrixMutation.isPending}
										className={cn(
											'hover:bg-transparent',
											matrix.active
												? 'text-kaefer-grass-green hover:text-kaefer-grass-green/70'
												: 'text-kaefer-medium-grey hover:text-kaefer-medium-grey/70',
										)}
										title={matrix.active ? 'Desativar matriz' : 'Ativar matriz'}
									>
										{matrix.active ? (
											<Power className="size-4" />
										) : (
											<PowerOff className="size-4" />
										)}
									</Button>
									<Button
										type="button"
										variant="ghost"
										size="icon-circle"
										onClick={() => setDeletingMatrix(matrix)}
										disabled={deleteMatrixMutation.isPending}
										className="text-kaefer-red hover:bg-transparent hover:text-kaefer-red/70"
										title="Excluir matriz"
									>
										<Trash2 className="size-4" />
									</Button>
								</div>
							</div>

							<div className="mt-3 flex flex-wrap gap-2">
								<Badge className="bg-kaefer-grass-green/15 text-kaefer-forest-green">
									{matrix.personIds.length} Pessoa
									{matrix.personIds.length === 1 ? '' : 's'}
								</Badge>
								<Badge className="bg-kaefer-sapphire-blue/15 text-kaefer-sapphire-blue">
									{matrix.crIds.length} CR
									{matrix.crIds.length === 1 ? '' : 's'}
								</Badge>
								<Badge className="bg-kaefer-violet/15 text-kaefer-violet">
									{matrix.jobGroupIds.length} Grupo
									{matrix.jobGroupIds.length === 1 ? '' : 's'}
								</Badge>
								<Badge className="bg-kaefer-honey-yellow/20 text-kaefer-dark-grey">
									{matrix.branchIds.length} Filial
									{matrix.branchIds.length === 1 ? '' : 'is'}
								</Badge>
							</div>
						</div>
					))
				)}
			</div>

			<ResponsiveFormModal
				open={matrixFormOpen}
				onOpenChange={setMatrixFormOpen}
				title={
					editingMatrix ? 'Editar matriz' : 'Nova matriz de comprometimento'
				}
				dialogClassName="max-h-[85vh] overflow-y-auto sm:max-w-2xl"
			>
				<CommitmentMatrixFormContent
					key={editingMatrix?.id ?? 'new'}
					mode={editingMatrix ? 'edit' : 'create'}
					initial={editingMatrix}
					onSave={handleSaveMatrix}
					onClose={() => setMatrixFormOpen(false)}
					isSaving={isSavingMatrix}
				/>
			</ResponsiveFormModal>

			<ConfirmDialog
				open={!!deletingMatrix}
				onOpenChange={(open) => {
					if (!open) setDeletingMatrix(null)
				}}
				title="Excluir matriz"
				onConfirm={handleConfirmDeleteMatrix}
				isPending={deleteMatrixMutation.isPending}
				destructive
			>
				Tem certeza que deseja excluir a matriz "{deletingMatrix?.name}"? Esta
				ação não pode ser desfeita.
			</ConfirmDialog>
		</div>
	)
}
